import React, { createContext, useContext, useState, useCallback } from 'react';
import { getTerm } from '@/lib/branding';

const ReportTabsContext = createContext(null);

export function ReportTabsProvider({ children }) {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  // Recently-closed insight tabs (most-recent first, capped) so a user can
  // re-open one they just closed. We keep the tab's draft in sessionStorage.
  const [closedTabs, setClosedTabs] = useState([]);

  const openTab = useCallback((reportId) => {
    const tabId = reportId || '_new_' + Date.now();
    setTabs(prev => {
      if (reportId) {
        const existing = prev.find(t => t.reportId === reportId);
        if (existing) {
          setActiveTabId(existing.id);
          // Re-opening an already-open tab (e.g. after adding a visual to it
          // elsewhere): bump a nonce so the builder re-fetches the latest saved
          // report instead of showing its stale in-memory snapshot.
          return prev.map(t => t.id === existing.id ? { ...t, reloadNonce: Date.now() } : t);
        }
      }
      const tab = { id: tabId, reportId, label: reportId ? 'Loading...' : `New ${getTerm('insights', 'singular')}` };
      return [...prev, tab];
    });
    setActiveTabId(tabId);
  }, []);

  // Open a brand-new report under a unique draft token (?draft=<token>) so
  // multiple new reports can be open simultaneously, each its own tab + chip.
  const openDraft = useCallback((token) => {
    if (!token) return;
    const tabId = `draft:${token}`;
    setTabs(prev => {
      if (prev.find(t => t.id === tabId)) return prev; // already open → just focus
      return [...prev, { id: tabId, reportId: null, label: `New ${getTerm('insights', 'singular')}` }];
    });
    setActiveTabId(tabId);
  }, []);

  const closeTab = useCallback((tabId) => {
    // "Discard & leave" means fully discard: drop this tab's in-progress draft
    // and crash-recovery snapshots so starting a NEW report is always blank and
    // never resurrects the one we just closed.
    setTabs(prev => {
      const closing = prev.find(t => t.id === tabId);
      try {
        sessionStorage.removeItem(`rb_draft_${tabId}`);
        // Unsaved/new tabs also shared a singleton draft slot — clear it too.
        if (!closing || !closing.reportId) sessionStorage.removeItem('rb_draft_new_singleton');
        if (closing && closing.reportId) sessionStorage.removeItem(`rb_recovery_${closing.reportId}`);
      } catch (_) { /* storage disabled */ }
      // Only SAVED reports are offered under "Recently closed" (they re-open
      // from the server); a discarded unsaved draft is gone for good.
      if (closing && closing.reportId) {
        setClosedTabs(cl => [{ id: tabId, reportId: closing.reportId, label: closing.label, hasDraft: false, at: Date.now() }, ...cl.filter(c => c.id !== tabId)].slice(0, 6));
      }
      const next = prev.filter(t => t.id !== tabId);
      if (activeTabId === tabId && next.length > 0) {
        setActiveTabId(next[next.length - 1].id);
      }
      if (next.length === 0) setActiveTabId(null);
      return next;
    });
  }, [activeTabId]);

  // Re-open a recently-closed tab. Saved insights re-open by reportId; unsaved
  // drafts re-open under their ORIGINAL id so the sessionStorage draft restores.
  const reopenClosed = useCallback((entry) => {
    if (!entry) return;
    setClosedTabs(cl => cl.filter(c => c.id !== entry.id));
    if (entry.reportId) { openTab(entry.reportId); return; }
    setTabs(prev => (prev.find(t => t.id === entry.id) ? prev : [...prev, { id: entry.id, reportId: null, label: entry.label || `New ${getTerm('insights', 'singular')}` }]));
    setActiveTabId(entry.id);
  }, [openTab]);

  const updateTab = useCallback((tabId, updates) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, ...updates } : t));
  }, []);

  return (
    <ReportTabsContext.Provider value={{ tabs, activeTabId, setActiveTabId, openTab, openDraft, closeTab, updateTab, closedTabs, reopenClosed }}>
      {children}
    </ReportTabsContext.Provider>
  );
}

export function useReportTabs() {
  return useContext(ReportTabsContext);
}
